import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Activity,
  Check,
  Eye,
  Link,
  PauseCircle,
  Plus,
  RefreshCcw,
  Upload,
  Workflow,
  X,
} from 'lucide-react';

import {
  MarketplaceAuditEvent,
  MarketplaceConfig,
  MarketplaceItem,
  MarketplaceItemDetail,
  MarketplaceMcpHealth,
  MarketplaceItemType,
  MarketplaceReviewQueue,
  MarketplaceTargetType,
  MarketplaceVersion,
  useAdminMarketplaceItem,
  useAdminMarketplaceItems,
  useBindPlatformMarketplaceTargets,
  useCreatePlatformMarketplaceItem,
  useCreatePlatformMarketplaceVersion,
  useMarketplaceAuditEvents,
  useMarketplaceConfig,
  useMarketplaceDecision,
  useMarketplaceMcpHealth,
  useMarketplaceReviewQueue,
  useMarketplaceTargetRegistry,
  useUpdateMarketplaceConfig,
} from '../../services/marketplace.service';

const defaultPayloadByType: Record<MarketplaceItemType, Record<string, unknown>> = {
  skill: {
    name: 'platform_support_triage',
    title: 'Platform Support Triage',
    short_description: 'Classify support conversations and suggest next steps.',
    instruction_content:
      'Classify the user request by urgency and return concise operator guidance.',
    risk_tags: [],
  },
  tool: {
    name: 'platform_lookup',
    title: 'Platform Lookup',
    description: 'Read approved platform data through a controlled webhook.',
    input_schema: {
      type: 'object',
      properties: { query: { type: 'string' } },
      required: ['query'],
    },
    endpoint_url: 'https://api.example.com/marketplace/platform-lookup',
    method: 'POST',
    auth_type: 'hmac_sha256',
    auth_secret_ref: 'secret:platform/lookup-signing-key',
    risk_level: 'read',
    tool_annotations: { readOnlyHint: true, openWorldHint: true, idempotentHint: true },
    requires_confirmation: false,
  },
  mcp_server: {
    server_label: 'platform_docs',
    display_name: 'Platform Docs MCP',
    description: 'Approved platform MCP server for documentation search.',
    server_url: 'https://mcp.example.com',
    transport: 'streamable_http',
    auth_config: { oauth_connection_ref: 'connection:platform-docs/oauth' },
    config_scope: 'admin',
    tool_snapshot: [
      {
        toolName: 'search_docs',
        description: 'Search approved documentation.',
        inputSchema: { type: 'object', properties: { q: { type: 'string' } }, required: ['q'] },
        annotations: { readOnlyHint: true, openWorldHint: true },
      },
    ],
  },
  plugin: {
    manifest: {
      name: 'cloudflare',
      version: '1.0.0',
      display_name: 'Cloudflare',
      description: 'Skills, docs MCP and helpers for Cloudflare Workers, D1, Durable Objects.',
      applies_to: ['cloudflare', 'workers', 'wrangler', 'd1', 'durable-objects'],
      detect: {
        files: ['wrangler.toml', 'wrangler.json', 'wrangler.jsonc'],
        deps: ['wrangler', '@cloudflare/workers-types', '@cloudflare/*'],
        keywords: ['cloudflare', 'worker', 'durable object', 'd1'],
      },
      mcp_servers: {},
      tools: [],
      hooks: {},
      min_cli_version: '0.1.0',
    },
    files: [
      {
        path: 'skills/cloudflare-worker/SKILL.md',
        content:
          '---\nname: cloudflare-worker\ndescription: Build, debug and deploy Cloudflare Workers with wrangler.\n---\n\n# Cloudflare Worker\n\nUse wrangler for local dev and deploy.\n',
      },
    ],
  },
};

const statusColor = (status: string) => {
  if (status.includes('approved')) return 'success';
  if (status.includes('pending') || status.includes('reviewed')) return 'warning';
  if (status.includes('rejected') || status.includes('suspended') || status === 'disabled') {
    return 'error';
  }
  return 'default';
};

const healthColor = (status: string) => {
  if (status === 'healthy') return 'success';
  if (status === 'warning') return 'warning';
  if (status === 'unavailable') return 'error';
  return 'default';
};

const driftColor = (status: string) => {
  if (status === 'ok') return 'success';
  if (status === 'warning') return 'warning';
  if (status === 'drift' || status === 'unavailable') return 'error';
  return 'default';
};

type DecisionAction =
  | 'approve-execution'
  | 'reject-execution'
  | 'approve-public'
  | 'reject-public'
  | 'suspend';

type AdminTab = 'review' | 'platform' | 'mcp' | 'config' | 'audit';

interface PlatformDraft {
  type: MarketplaceItemType;
  name: string;
  title: string;
  description: string;
  visibility: 'private' | 'public';
}

export const MarketplaceManagement: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('review');
  const [queue, setQueue] = useState<MarketplaceReviewQueue>('execution');
  const [decision, setDecision] = useState<{
    action: DecisionAction;
    version: MarketplaceVersion;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [detailItemId, setDetailItemId] = useState<string | undefined>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [versionItem, setVersionItem] = useState<MarketplaceItem | null>(null);
  const [bindItem, setBindItem] = useState<MarketplaceItem | null>(null);
  const [draftConfig, setDraftConfig] = useState<MarketplaceConfig | null>(null);
  const [platformDraft, setPlatformDraft] = useState(() => createPlatformDraft('skill'));
  const [versionPayloadText, setVersionPayloadText] = useState(
    JSON.stringify(defaultPayloadByType.skill, null, 2)
  );
  const [semver, setSemver] = useState('');
  const [targetType, setTargetType] = useState<MarketplaceTargetType>('agent_template');
  const [targetId, setTargetId] = useState('default_user_agent');
  const [runtimePolicy, setRuntimePolicy] = useState<'default' | 'allow' | 'ask' | 'deny'>(
    'default'
  );

  const { data: queueResponse, isLoading, isFetching, refetch } = useMarketplaceReviewQueue(queue);
  const { data: config } = useMarketplaceConfig();
  const platformItemsQuery = useAdminMarketplaceItems({ ownerScope: 'platform' });
  const mcpHealthQuery = useMarketplaceMcpHealth();
  const auditEventsQuery = useMarketplaceAuditEvents();
  const targetRegistryQuery = useMarketplaceTargetRegistry();
  const detailQuery = useAdminMarketplaceItem(detailItemId);
  const updateConfig = useUpdateMarketplaceConfig();
  const createPlatformItem = useCreatePlatformMarketplaceItem();
  const createPlatformVersion = useCreatePlatformMarketplaceVersion();
  const bindPlatformTargets = useBindPlatformMarketplaceTargets();
  const approveExecution = useMarketplaceDecision('approve-execution');
  const rejectExecution = useMarketplaceDecision('reject-execution');
  const approvePublic = useMarketplaceDecision('approve-public');
  const rejectPublic = useMarketplaceDecision('reject-public');
  const suspend = useMarketplaceDecision('suspend');

  useEffect(() => {
    if (config) setDraftConfig(config);
  }, [config]);

  useEffect(() => {
    const payload = defaultPayloadByType[platformDraft.type];
    setPlatformDraft((current) => ({
      ...current,
      name: String(payload.name || payload.server_label),
      title: String(payload.title || payload.display_name),
      description: String(payload.description || payload.short_description || ''),
    }));
    setVersionPayloadText(JSON.stringify(payload, null, 2));
  }, [platformDraft.type]);

  const activeMutation = {
    'approve-execution': approveExecution,
    'reject-execution': rejectExecution,
    'approve-public': approvePublic,
    'reject-public': rejectPublic,
    suspend,
  }[decision?.action ?? 'approve-execution'];

  const queueRows = queueResponse?.data ?? [];
  const platformRows = platformItemsQuery.data ?? [];
  const mcpHealthRows = mcpHealthQuery.data ?? [];
  const auditRows = auditEventsQuery.data ?? [];

  const targetOptions = useMemo(() => {
    if (targetType === 'agent_template') {
      return (
        targetRegistryQuery.data?.agent_templates.map((target) => ({
          id: target.key,
          label: target.name,
        })) ?? []
      );
    }
    if (targetType === 'InternalAITask') {
      return (
        targetRegistryQuery.data?.internal_ai_tasks.map((target) => ({
          id: target.publicId,
          label: `${target.taskKey} - ${target.name}`,
        })) ?? []
      );
    }
    return [];
  }, [targetRegistryQuery.data, targetType]);

  useEffect(() => {
    if (targetType === 'agent_template') setTargetId(targetOptions[0]?.id ?? 'default_user_agent');
    if (targetType === 'InternalAITask') setTargetId(targetOptions[0]?.id ?? '');
  }, [targetOptions, targetType]);

  const reviewColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'item',
        headerName: 'Item',
        flex: 1.2,
        minWidth: 240,
        valueGetter: (_, row: MarketplaceVersion) => row.manifest?.title || row.manifest?.name,
        renderCell: (params) => (
          <Box className="flex h-full min-w-0 flex-col justify-center">
            <Typography variant="body2" className="truncate font-semibold">
              {String(params.value || 'Untitled')}
            </Typography>
            <Typography
              variant="caption"
              className="truncate font-mono text-gray-500 dark:text-slate-400"
            >
              {params.row.publicId}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 130,
        valueGetter: (_, row: MarketplaceVersion) => row.manifest?.type,
        renderCell: (params) => <Chip size="small" label={String(params.value)} />,
      },
      {
        field: 'riskLevel',
        headerName: 'Risk',
        width: 140,
        renderCell: (params) => <Chip size="small" color="warning" label={params.value} />,
      },
      {
        field: 'executionStatus',
        headerName: 'Execution',
        width: 190,
        renderCell: (params) => (
          <Chip size="small" color={statusColor(params.value)} label={params.value} />
        ),
      },
      {
        field: 'publicationStatus',
        headerName: 'Publication',
        width: 190,
        renderCell: (params) => (
          <Chip size="small" color={statusColor(params.value)} label={params.value} />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 350,
        sortable: false,
        renderCell: (params) => {
          const version = params.row as MarketplaceVersion;
          return (
            <Box className="flex h-full items-center gap-1">
              <Button
                size="small"
                startIcon={<Eye size={16} />}
                onClick={() => openDetail(version.itemPublicId)}
              >
                Detail
              </Button>
              {queue === 'execution' ? (
                <>
                  <Button
                    size="small"
                    startIcon={<Check size={16} />}
                    onClick={() => setDecision({ action: 'approve-execution', version })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<X size={16} />}
                    onClick={() => setDecision({ action: 'reject-execution', version })}
                  >
                    Reject
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    startIcon={<Check size={16} />}
                    onClick={() => setDecision({ action: 'approve-public', version })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<X size={16} />}
                    onClick={() => setDecision({ action: 'reject-public', version })}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button
                size="small"
                color="warning"
                startIcon={<PauseCircle size={16} />}
                onClick={() => setDecision({ action: 'suspend', version })}
              >
                Suspend
              </Button>
            </Box>
          );
        },
      },
    ],
    [queue]
  );

  const platformColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'title',
        headerName: 'Platform item',
        flex: 1.2,
        minWidth: 260,
        renderCell: (params) => (
          <Box className="flex h-full min-w-0 flex-col justify-center">
            <Typography variant="body2" className="truncate font-semibold">
              {params.row.title}
            </Typography>
            <Typography
              variant="caption"
              className="truncate font-mono text-gray-500 dark:text-slate-400"
            >
              {params.row.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 130,
        renderCell: (params) => <Chip size="small" label={params.value} />,
      },
      { field: 'visibility', headerName: 'Visibility', width: 130 },
      {
        field: 'executionStatus',
        headerName: 'Execution',
        width: 190,
        renderCell: (params) => (
          <Chip size="small" color={statusColor(params.value)} label={params.value} />
        ),
      },
      {
        field: 'latestVersion',
        headerName: 'Latest',
        width: 130,
        valueGetter: (_, row: MarketplaceItem) => row.latestVersion?.versionNumber,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 310,
        sortable: false,
        renderCell: (params) => {
          const item = params.row as MarketplaceItem;
          return (
            <Box className="flex h-full items-center gap-1">
              <Button
                size="small"
                startIcon={<Eye size={16} />}
                onClick={() => openDetail(item.publicId)}
              >
                Detail
              </Button>
              <Button
                size="small"
                startIcon={<Upload size={16} />}
                onClick={() => openVersionDialog(item)}
              >
                Version
              </Button>
              <Button
                size="small"
                startIcon={<Link size={16} />}
                disabled={item.executionStatus !== 'approved_for_execution'}
                onClick={() => openBindDialog(item)}
              >
                Bind
              </Button>
            </Box>
          );
        },
      },
    ],
    []
  );

  const auditColumns = useMemo<GridColDef<MarketplaceAuditEvent>[]>(
    () => [
      { field: 'createdAt', headerName: 'Time', width: 190 },
      { field: 'action', headerName: 'Action', flex: 1, minWidth: 240 },
      { field: 'resourceType', headerName: 'Resource', width: 210 },
      { field: 'actorType', headerName: 'Actor', width: 120 },
      {
        field: 'success',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            color={params.value ? 'success' : 'error'}
            label={params.value ? 'success' : 'failed'}
          />
        ),
      },
    ],
    []
  );

  const mcpHealthColumns = useMemo<GridColDef<MarketplaceMcpHealth>[]>(
    () => [
      {
        field: 'itemTitle',
        headerName: 'MCP server',
        flex: 1.1,
        minWidth: 240,
        renderCell: (params) => (
          <Box className="flex h-full min-w-0 flex-col justify-center">
            <Typography variant="body2" className="truncate font-semibold">
              {params.row.itemTitle || params.row.itemName || params.row.serverLabel}
            </Typography>
            <Typography
              variant="caption"
              className="truncate font-mono text-gray-500 dark:text-slate-400"
            >
              {params.row.serverHost || params.row.versionPublicId}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'healthStatus',
        headerName: 'Health',
        width: 130,
        renderCell: (params) => (
          <Chip size="small" color={healthColor(params.value)} label={params.value} />
        ),
      },
      {
        field: 'driftStatus',
        headerName: 'Drift',
        width: 130,
        renderCell: (params) => (
          <Chip size="small" color={driftColor(params.value)} label={params.value} />
        ),
      },
      { field: 'snapshotCount', headerName: 'Snapshot', width: 110 },
      {
        field: 'missingExpectedTools',
        headerName: 'Missing',
        width: 110,
        valueGetter: (_, row) => row.missingExpectedTools.length + row.missingAllowedTools.length,
      },
      {
        field: 'unexpectedSnapshotTools',
        headerName: 'Unexpected',
        width: 130,
        valueGetter: (_, row) => row.unexpectedSnapshotTools.length,
      },
      { field: 'executionStatus', headerName: 'Execution', width: 190 },
      { field: 'publicationStatus', headerName: 'Publication', width: 180 },
      { field: 'lastSnapshotAt', headerName: 'Last snapshot', width: 190 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<Eye size={16} />}
            onClick={() => openDetail(params.row.itemPublicId)}
          >
            Detail
          </Button>
        ),
      },
    ],
    []
  );

  const saveDecision = async () => {
    if (!decision) return;
    await activeMutation.mutateAsync({
      versionPublicId: decision.version.publicId,
      reason: reason.trim() || undefined,
    });
    setDecision(null);
    setReason('');
    void detailQuery.refetch();
  };

  const createPlatform = async () => {
    const payload = parsePayload(versionPayloadText);
    if (!payload) return;
    const item = await createPlatformItem.mutateAsync(platformDraft);
    if (!item) return;
    await createPlatformVersion.mutateAsync({
      itemPublicId: item.publicId,
      input: { semver: semver.trim() || undefined, payload },
    });
    setCreateOpen(false);
    setSemver('');
    setPlatformDraft(createPlatformDraft(platformDraft.type));
  };

  const createVersion = async () => {
    if (!versionItem) return;
    const payload = parsePayload(versionPayloadText);
    if (!payload) return;
    await createPlatformVersion.mutateAsync({
      itemPublicId: versionItem.publicId,
      input: { semver: semver.trim() || undefined, payload },
    });
    setVersionItem(null);
    setSemver('');
  };

  const bindTarget = async () => {
    if (!bindItem || !targetId) return;
    await bindPlatformTargets.mutateAsync({
      itemPublicId: bindItem.publicId,
      targets: [
        {
          target_type: targetType,
          target_id: targetId,
          pinned_version_public_id: bindItem.activeExecutionVersion?.publicId ?? undefined,
          runtime_policy_override: runtimePolicy === 'default' ? undefined : runtimePolicy,
        },
      ],
    });
    setBindItem(null);
  };

  const reasonRequired =
    decision?.action === 'reject-execution' ||
    decision?.action === 'reject-public' ||
    decision?.action === 'suspend';

  function openDetail(itemPublicId?: string | null) {
    if (!itemPublicId) return;
    setDetailItemId(itemPublicId);
    setDetailOpen(true);
  }

  function openVersionDialog(item: MarketplaceItem) {
    setVersionItem(item);
    setSemver('');
    setVersionPayloadText(JSON.stringify(defaultPayloadByType[item.type], null, 2));
  }

  function openBindDialog(item: MarketplaceItem) {
    setBindItem(item);
    setTargetType('agent_template');
    setTargetId('default_user_agent');
    setRuntimePolicy('default');
  }

  function parsePayload(text: string) {
    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      window.alert('Payload JSON is invalid.');
      return null;
    }
  }

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      <Box className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Marketplace
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Review, publish, author, bind, and audit marketplace skills, webhook tools, and MCP
            servers.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
          onClick={() => {
            void refetch();
            void platformItemsQuery.refetch();
            void mcpHealthQuery.refetch();
            void auditEventsQuery.refetch();
          }}
        >
          Refresh
        </Button>
      </Box>

      <Paper className="overflow-hidden border border-gray-200 dark:border-slate-700 dark:bg-slate-800">
        <Box className="border-b border-gray-200 px-4 dark:border-slate-700">
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="review" label="Review queue" />
            <Tab value="platform" label="Platform items" />
            <Tab value="mcp" label="MCP health" />
            <Tab value="config" label="Config" />
            <Tab value="audit" label="Audit events" />
          </Tabs>
        </Box>

        {tab === 'review' && (
          <Box>
            <Box className="border-b border-gray-200 px-4 dark:border-slate-700">
              <Tabs value={queue} onChange={(_, value) => setQueue(value)}>
                <Tab value="execution" label="Execution review" />
                <Tab value="publication" label="Public review" />
              </Tabs>
            </Box>
            <Box className="h-[640px]">
              {isLoading ? (
                <Box className="flex h-full items-center justify-center">
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={queueRows}
                  columns={reviewColumns}
                  getRowId={(row) => row.publicId}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                />
              )}
            </Box>
          </Box>
        )}

        {tab === 'platform' && (
          <Box>
            <Box className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-slate-700">
              <Box className="flex items-center gap-2">
                <Workflow size={20} />
                <Typography variant="h6" className="font-semibold">
                  Platform authoring
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                Create platform item
              </Button>
            </Box>
            <Box className="h-[640px]">
              {platformItemsQuery.isLoading ? (
                <Box className="flex h-full items-center justify-center">
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={platformRows}
                  columns={platformColumns}
                  getRowId={(row) => row.publicId}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                />
              )}
            </Box>
          </Box>
        )}

        {tab === 'mcp' && (
          <Box>
            <Box className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-slate-700">
              <Box className="flex items-center gap-2">
                <Activity size={20} />
                <Typography variant="h6" className="font-semibold">
                  MCP health and drift
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={
                  mcpHealthQuery.isFetching ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshCcw size={16} />
                  )
                }
                onClick={() => void mcpHealthQuery.refetch()}
              >
                Refresh
              </Button>
            </Box>
            <Box className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.6fr)]">
              <Box className="h-[620px]">
                {mcpHealthQuery.isLoading ? (
                  <Box className="flex h-full items-center justify-center">
                    <CircularProgress />
                  </Box>
                ) : (
                  <DataGrid
                    rows={mcpHealthRows}
                    columns={mcpHealthColumns}
                    getRowId={(row) => row.versionPublicId}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  />
                )}
              </Box>
              <Box className="space-y-3">
                <Alert severity="info">
                  Health is derived from approved version metadata and stored MCP tool snapshots.
                </Alert>
                {mcpHealthRows.slice(0, 5).map((row) => (
                  <Paper
                    key={row.versionPublicId}
                    variant="outlined"
                    className="space-y-2 p-3 dark:border-slate-700"
                  >
                    <Box className="flex flex-wrap items-center gap-2">
                      <Chip
                        size="small"
                        color={healthColor(row.healthStatus)}
                        label={row.healthStatus}
                      />
                      <Chip
                        size="small"
                        color={driftColor(row.driftStatus)}
                        label={row.driftStatus}
                      />
                      <Typography variant="body2" className="font-semibold">
                        {row.itemTitle || row.serverLabel}
                      </Typography>
                    </Box>
                    <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
                      {row.diagnostic}
                    </Typography>
                    {(row.missingExpectedTools.length > 0 ||
                      row.missingAllowedTools.length > 0 ||
                      row.unexpectedSnapshotTools.length > 0) && (
                      <JsonBlock
                        title="Drift"
                        value={{
                          missingExpectedTools: row.missingExpectedTools,
                          missingAllowedTools: row.missingAllowedTools,
                          unexpectedSnapshotTools: row.unexpectedSnapshotTools,
                        }}
                      />
                    )}
                  </Paper>
                ))}
                {!mcpHealthQuery.isLoading && mcpHealthRows.length === 0 && (
                  <Alert severity="info">No MCP marketplace versions found.</Alert>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {tab === 'config' && (
          <Box className="max-w-2xl space-y-4 p-5">
            {draftConfig ? (
              <>
                <Box className="grid gap-4 md:grid-cols-2">
                  <FormControl fullWidth size="small">
                    <InputLabel>Execution mode</InputLabel>
                    <Select
                      label="Execution mode"
                      value={draftConfig.execution_review_mode}
                      onChange={(event) =>
                        setDraftConfig({
                          ...draftConfig,
                          execution_review_mode: event.target.value as 'manual' | 'ai_assisted',
                        })
                      }
                    >
                      <MenuItem value="manual">manual</MenuItem>
                      <MenuItem value="ai_assisted">ai_assisted</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Publication mode</InputLabel>
                    <Select
                      label="Publication mode"
                      value={draftConfig.publication_review_mode}
                      onChange={(event) =>
                        setDraftConfig({
                          ...draftConfig,
                          publication_review_mode: event.target.value as 'manual' | 'ai_assisted',
                        })
                      }
                    >
                      <MenuItem value="manual">manual</MenuItem>
                      <MenuItem value="ai_assisted">ai_assisted</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {[
                  ['execution_auto_approve_low_risk', 'Auto-approve low-risk execution'],
                  ['publication_auto_approve_low_risk', 'Auto-approve low-risk publication'],
                  ['user_mcp_creation_enabled', 'Enable user MCP creation'],
                ].map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={Boolean(draftConfig[key as keyof MarketplaceConfig])}
                        onChange={(event) =>
                          setDraftConfig({ ...draftConfig, [key]: event.target.checked })
                        }
                      />
                    }
                    label={label}
                  />
                ))}
                <Button
                  variant="contained"
                  disabled={updateConfig.isPending}
                  onClick={() => draftConfig && updateConfig.mutate(draftConfig)}
                >
                  Save config
                </Button>
              </>
            ) : (
              <CircularProgress />
            )}
          </Box>
        )}

        {tab === 'audit' && (
          <Box className="h-[640px]">
            {auditEventsQuery.isLoading ? (
              <Box className="flex h-full items-center justify-center">
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={auditRows}
                columns={auditColumns}
                getRowId={(row) => row.publicId}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              />
            )}
          </Box>
        )}
      </Paper>

      <DetailDialog
        open={detailOpen}
        loading={detailQuery.isLoading}
        item={detailQuery.data ?? null}
        onClose={() => setDetailOpen(false)}
      />

      <Dialog open={Boolean(decision)} onClose={() => setDecision(null)} fullWidth maxWidth="sm">
        <DialogTitle>{decision?.action.replace('-', ' ')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={4}
            margin="dense"
            label={reasonRequired ? 'Reason' : 'Notes'}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required={reasonRequired}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              decision?.action.includes('reject') || decision?.action === 'suspend'
                ? 'error'
                : 'primary'
            }
            disabled={activeMutation.isPending || (reasonRequired && !reason.trim())}
            onClick={() => void saveDecision()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <PlatformAuthoringDialog
        open={createOpen}
        title="Create platform item"
        draft={platformDraft}
        semver={semver}
        payloadText={versionPayloadText}
        pending={createPlatformItem.isPending || createPlatformVersion.isPending}
        onDraftChange={setPlatformDraft}
        onSemverChange={setSemver}
        onPayloadTextChange={setVersionPayloadText}
        onClose={() => setCreateOpen(false)}
        onSave={() => void createPlatform()}
      />

      <VersionDialog
        item={versionItem}
        semver={semver}
        payloadText={versionPayloadText}
        pending={createPlatformVersion.isPending}
        onSemverChange={setSemver}
        onPayloadTextChange={setVersionPayloadText}
        onClose={() => setVersionItem(null)}
        onSave={() => void createVersion()}
      />

      <Dialog open={Boolean(bindItem)} onClose={() => setBindItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>Bind platform item</DialogTitle>
        <DialogContent className="space-y-4">
          <Alert severity="info">
            Bindings apply at runtime resolution. Agent template bindings are inherited by matching
            agents.
          </Alert>
          <FormControl fullWidth size="small">
            <InputLabel>Target type</InputLabel>
            <Select
              label="Target type"
              value={targetType}
              onChange={(event) => setTargetType(event.target.value as MarketplaceTargetType)}
            >
              <MenuItem value="agent_template">Agent template</MenuItem>
              <MenuItem value="InternalAITask">Internal AI task</MenuItem>
              <MenuItem value="agent">Agent public id</MenuItem>
            </Select>
          </FormControl>
          {targetType === 'agent' ? (
            <TextField
              fullWidth
              size="small"
              label="Agent public id"
              value={targetId}
              onChange={(event) => setTargetId(event.target.value)}
            />
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Target</InputLabel>
              <Select
                label="Target"
                value={targetId}
                onChange={(event) => setTargetId(event.target.value)}
              >
                {targetOptions.map((target) => (
                  <MenuItem key={target.id} value={target.id}>
                    {target.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Runtime policy</InputLabel>
            <Select
              label="Runtime policy"
              value={runtimePolicy}
              onChange={(event) => setRuntimePolicy(event.target.value as typeof runtimePolicy)}
            >
              <MenuItem value="default">default</MenuItem>
              <MenuItem value="allow">allow</MenuItem>
              <MenuItem value="ask">ask</MenuItem>
              <MenuItem value="deny">deny</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBindItem(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={bindPlatformTargets.isPending || !targetId}
            onClick={() => void bindTarget()}
          >
            Bind
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function createPlatformDraft(type: MarketplaceItemType): PlatformDraft {
  const payload = defaultPayloadByType[type];
  return {
    type,
    name: String(payload.name || payload.server_label),
    title: String(payload.title || payload.display_name),
    description: String(payload.description || payload.short_description || ''),
    visibility: 'private' as const,
  };
}

function DetailDialog({
  open,
  loading,
  item,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  item: MarketplaceItemDetail | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Marketplace detail</DialogTitle>
      <DialogContent className="space-y-4">
        {loading && <CircularProgress />}
        {!loading && !item && <Alert severity="warning">Marketplace item was not found.</Alert>}
        {item && (
          <>
            <Box className="flex flex-wrap items-center gap-2">
              <Typography variant="h6" className="font-semibold">
                {item.title}
              </Typography>
              <Chip size="small" label={item.type} />
              <Chip
                size="small"
                color={statusColor(item.executionStatus)}
                label={item.executionStatus}
              />
              <Chip
                size="small"
                color={statusColor(item.publicationStatus)}
                label={item.publicationStatus}
              />
              <Chip size="small" label={item.ownerScope} />
            </Box>
            <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
              {item.description || item.name}
            </Typography>
            <Divider />
            <Box className="grid gap-4 lg:grid-cols-2">
              <Box className="space-y-3">
                <Typography variant="subtitle1" className="font-semibold">
                  Versions
                </Typography>
                {item.versions?.map((version: MarketplaceVersion) => (
                  <Paper
                    key={version.publicId}
                    variant="outlined"
                    className="space-y-3 p-3 dark:border-slate-700"
                  >
                    <Box className="flex flex-wrap items-center gap-2">
                      <Chip size="small" label={`v${version.versionNumber}`} />
                      <Chip
                        size="small"
                        color={statusColor(version.executionStatus)}
                        label={version.executionStatus}
                      />
                      <Chip
                        size="small"
                        color={statusColor(version.publicationStatus)}
                        label={version.publicationStatus}
                      />
                      <Chip size="small" color="warning" label={version.riskLevel} />
                    </Box>
                    <JsonBlock title="Manifest" value={version.manifest} />
                    <JsonBlock title="Validation findings" value={version.validationFindings} />
                    {version.mcpToolSnapshots && version.mcpToolSnapshots.length > 0 && (
                      <JsonBlock title="MCP tool snapshot" value={version.mcpToolSnapshots} />
                    )}
                  </Paper>
                ))}
              </Box>
              <Box className="space-y-3">
                <Typography variant="subtitle1" className="font-semibold">
                  Review events
                </Typography>
                {item.versions?.flatMap((version: MarketplaceVersion) =>
                  (version.reviews ?? []).map((review) => (
                    <Paper
                      key={review.publicId}
                      variant="outlined"
                      className="p-3 dark:border-slate-700"
                    >
                      <Box className="mb-2 flex flex-wrap items-center gap-2">
                        <Chip size="small" label={review.reviewType} />
                        <Chip size="small" label={review.verdict} />
                        <Chip size="small" color="warning" label={review.severity} />
                        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                          {review.createdAt}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{review.notes || 'No notes'}</Typography>
                      {review.findings && review.findings.length > 0 && (
                        <JsonBlock title="Findings" value={review.findings} />
                      )}
                    </Paper>
                  ))
                )}
                <Typography variant="subtitle1" className="font-semibold">
                  Bindings
                </Typography>
                <JsonBlock
                  title="Active bindings/installations"
                  value={{ bindings: item.bindings, installations: item.installations }}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function PlatformAuthoringDialog({
  open,
  title,
  draft,
  semver,
  payloadText,
  pending,
  onDraftChange,
  onSemverChange,
  onPayloadTextChange,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  draft: ReturnType<typeof createPlatformDraft>;
  semver: string;
  payloadText: string;
  pending: boolean;
  onDraftChange: (draft: ReturnType<typeof createPlatformDraft>) => void;
  onSemverChange: (value: string) => void;
  onPayloadTextChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className="space-y-4">
        <Box className="grid gap-3 pt-2 md:grid-cols-3">
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={draft.type}
              onChange={(event) =>
                onDraftChange({ ...draft, type: event.target.value as MarketplaceItemType })
              }
            >
              <MenuItem value="skill">Skill</MenuItem>
              <MenuItem value="tool">Webhook tool</MenuItem>
              <MenuItem value="mcp_server">MCP server</MenuItem>
              <MenuItem value="plugin">Plugin (skills + MCP + tools bundle)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Name"
            value={draft.name}
            onChange={(event) => onDraftChange({ ...draft, name: event.target.value })}
          />
          <TextField
            size="small"
            label="Title"
            value={draft.title}
            onChange={(event) => onDraftChange({ ...draft, title: event.target.value })}
          />
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={draft.description}
          onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
        />
        <Box className="grid gap-3 md:grid-cols-2">
          <TextField
            size="small"
            label="Semver"
            value={semver}
            onChange={(event) => onSemverChange(event.target.value)}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Visibility</InputLabel>
            <Select
              label="Visibility"
              value={draft.visibility}
              onChange={(event) =>
                onDraftChange({ ...draft, visibility: event.target.value as 'private' | 'public' })
              }
            >
              <MenuItem value="private">private</MenuItem>
              <MenuItem value="public">public</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={18}
          label="Version payload JSON"
          value={payloadText}
          onChange={(event) => onPayloadTextChange(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending} onClick={onSave}>
          Create and approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VersionDialog({
  item,
  semver,
  payloadText,
  pending,
  onSemverChange,
  onPayloadTextChange,
  onClose,
  onSave,
}: {
  item: MarketplaceItem | null;
  semver: string;
  payloadText: string;
  pending: boolean;
  onSemverChange: (value: string) => void;
  onPayloadTextChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New version{item ? ` for ${item.title}` : ''}</DialogTitle>
      <DialogContent className="space-y-4">
        <TextField
          fullWidth
          size="small"
          label="Semver"
          value={semver}
          onChange={(event) => onSemverChange(event.target.value)}
        />
        <TextField
          fullWidth
          multiline
          minRows={18}
          label="Version payload JSON"
          value={payloadText}
          onChange={(event) => onPayloadTextChange(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending} onClick={onSave}>
          Create version
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <Box>
      <Typography
        variant="caption"
        className="font-semibold uppercase text-gray-500 dark:text-slate-400"
      >
        {title}
      </Typography>
      <Box
        component="pre"
        className="mt-1 max-h-72 overflow-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-900"
      >
        {JSON.stringify(value ?? null, null, 2)}
      </Box>
    </Box>
  );
}
